/**
 * Fereastra de adresa — logica PURA din jurul cautarii si al hartii, fara
 * React, ca sa poata fi testata:
 *
 *  - numarul scris de om se pastreaza cand furnizorul da doar strada;
 *  - eticheta de sub fiecare candidat spune exact ce s-a verificat (in zona,
 *    in alta localitate decat cea ceruta) — nu un „nu am gasit" sec;
 *  - de unde porneste harta cand se deschide.
 */

import type { GeoPoint, GeocodeCandidate } from "@deliveryapp/shared";

/**
 * Numarul de imobil al schitei: al furnizorului cand il are, altfel cel scris
 * de om. Inainte, „Strada Mierlei 12" gasea strada (fara numere in OSM) si
 * omul retasta „12" la pasul urmator.
 */
export function candidateStreetNumber(
  candidate: Pick<GeocodeCandidate, "streetNumber">,
  typed: string | null | undefined,
): string {
  const fromProvider = candidate.streetNumber?.trim() ?? "";
  if (fromProvider.length > 0) return fromProvider;
  return typed?.trim() ?? "";
}

export interface CandidateNote {
  tone: "warning" | "success";
  text: string;
}

/**
 * Ce spune randul candidatului, in ordinea a ceea ce conteaza pentru client:
 *  - in afara zonelor pietei → avertisment (oricat de bine s-ar potrivi textul);
 *  - in alta localitate decat cea ceruta → avertisment, dar cu vestea buna cand
 *    e totusi in zona de livrare („nu e in Urseni, dar e in zona") — asta e
 *    cazul strazii pe care furnizorul o are doar in satul vecin;
 *  - in zona → confirmare scurta;
 *  - altfel nimic (core vechi, piata fara contururi).
 */
export function candidateNote(
  candidate: Pick<GeocodeCandidate, "inCoverage" | "cityMatch">,
  wantedCity: string | null | undefined,
): CandidateNote | null {
  if (candidate.inCoverage === false) return { tone: "warning", text: "În afara zonei de livrare" };
  const wanted = typeof wantedCity === "string" && wantedCity.trim().length > 0 ? wantedCity.trim() : null;
  if (candidate.cityMatch === false && wanted !== null) {
    return {
      tone: "warning",
      text: candidate.inCoverage === true ? `Nu e în ${wanted}, dar e în zona de livrare` : `Nu e în ${wanted}`,
    };
  }
  if (candidate.inCoverage === true) return { tone: "success", text: "În zona de livrare" };
  return null;
}

/** Textul de deasupra listei cand core-ul a cautat FARA localitatea ceruta. */
export function relaxedNotice(cityHint: string | null | undefined, relaxed: boolean | undefined): string | null {
  const city = typeof cityHint === "string" && cityHint.trim().length > 0 ? cityHint.trim() : null;
  if (relaxed !== true || city === null) return null;
  return `Nu am găsit strada în ${city}. Iată ce am găsit în restul zonei de livrare:`;
}

/**
 * De unde porneste harta, in ordinea preciziei: punctul schitei (ajustare),
 * primul candidat gasit, adresa din sesiune, centrul contururilor pietei.
 * `null` = nu e de unde: apelantul incearca localitatea sau locatia.
 */
export function initialMapCenter(sources: {
  draft?: GeoPoint | null;
  candidate?: GeoPoint | null;
  session?: GeoPoint | null;
  coverageCenter?: GeoPoint | null;
}): GeoPoint | null {
  for (const point of [sources.draft, sources.candidate, sources.session, sources.coverageCenter]) {
    if (point !== null && point !== undefined && Number.isFinite(point.lat) && Number.isFinite(point.lng)) {
      return { lat: point.lat, lng: point.lng };
    }
  }
  return null;
}
